import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as search from "@client/features/search";
import * as forms from "@client/forms";
import * as router from "@client/router";
import {AppDispatch} from "@client/store";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import React from "react";

export const confirmAndDeleteFood =
    (food: server.journal.nutrition.Food.output) => async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body: "This will be deleted forever.",
            }),
        );
        if (confirmed) {
            await server.journal.nutrition.delete_food({food_id: food.id});
            void dispatch(search.loadSearch({reset: true}));
            dispatch(notify(`Your food was deleted`));
            void router.push("food_list.index", {});
        }
    };

/**
 * Fakes a food to behave like an entry so we can use it in EntryList.
 */
export const getFilteredFoodsAsCalculatableEntries = (
    filter: string,
    foods: server.journal.nutrition.Food.output[],
): Array<{
    entry: server.journal.nutrition.InlineEntry.output;
    quantity: string | null;
    unit: string;
    food_id: number;
}> => {
    return foods
        .filter((food) => utils.filterPredicate(food.name, filter))
        .map((food) => ({
            ...food,
            // Not all foods have a base unit (example: corrected foods from the database that came in as "1 package"
            // So we need to fall back to this when treating this as a calculatable entry on the front end.
            quantity:
                food.base_quantity != null
                    ? food.base_quantity
                    : food.serving_size_numerator!,
            unit: food.base_unit ?? food.serving_size_unit ?? "serving",
        }))
        .map((food) => ({
            entry: {
                ...food,
                upc: food.upc ?? "",
                base_unit: food.base_unit ?? "",
                serving_size_unit: food.serving_size_unit ?? "",
                uuid: "" as UUID,
                container_quantity: null,
                container_unit: "",

                container_servings: food.container_servings ?? "1",
                serving_size_numerator: food.serving_size_numerator ?? "1",
                serving_size_denominator: "1",
                nutritional_vision_prompt: null,
                icon: null,
                backend: "foods",
                backend_id: food.id.toString(),
                usda_food_id: null,
                expanded_recipe: [],
                additional_units: [],
                units: [
                    {
                        unit: food.base_unit ?? "",
                        multiplier: food.base_quantity ?? "1",
                    },
                    {
                        unit: food.serving_size_unit ?? "",
                        multiplier: food.serving_size_numerator ?? "1",
                    },
                ],
            },
            food_id: food.id,
            // Not all foods have a base unit (example: corrected foods from the database that came in as "1 package"
            // So we need to fall back to this when treating this as a calculatable entry on the front end.
            quantity: food.quantity,
            unit: food.unit,
        }));
};

export const useFoodForms = ({
    initial,
}: {
    initial?: Partial<server.journal.nutrition.CreateFood.values> | null;
}) => {
    const form = forms.useForm(server.journal.nutrition.CreateFood, {
        initial,
    });

    const layout = forms.layout(form, {
        sections: [
            {
                name: "basic",
                label: "Basics",
                description:
                    "Tell us a little about this item. You must tell us how much the food weighs, or its serving size information. Both is even better.",
                fields: [
                    "name",
                    {
                        row: [
                            {name: "base_unit", label: null},
                            {
                                name: "base_quantity",
                                label: null,
                                when:
                                    form.values.base_unit != null &&
                                    form.values.base_unit !== "",
                            },
                        ],
                        label: "How much does each serving weigh?",
                    },
                    {
                        name: "container_servings",
                        label: "How many servings are in the container?",
                    },
                    {
                        name: "serving_size_unit",
                        label: "What do you typically call a serving? A slice? A cup?",
                    },
                    {
                        name: "serving_size_numerator",
                        label: "What is the serving quantity? Ex: For 2 slices write 2.",
                    },
                    {text: "Now tell us about its nutrition"},
                    {name: "calories_pg", label: "Energy per serving"},
                    "protein_pg",
                    "fat_pg",
                    "carbs_pg",
                    "fiber_pg",
                    {text: "And optionally some advanced information"},
                    "net_carbs_pg",
                    "upc",
                ],
            },
            {
                name: "advanced",
                label: "Advanced",
                description: "Tell us about its macro breakdown.",
                fields: [
                    "saturated_fat_pg",
                    "polyunsaturated_fat_pg",
                    "trans_fat_pg",
                    "cholesterol_pg",
                    "sugars_pg",
                    {text: "Tell us about its vitamins and minerals"},
                    "potassium_pg",
                    "sodium_pg",
                    "magnesium_pg",
                    "vitamin_a_dv_pg",
                    "vitamin_c_dv_pg",
                    "calcium_dv_pg",
                    "iron_dv_pg",
                ],
            },
        ],
        hidden: ["from_missing_upc", "monounsaturated_fat_pg", "added_sugars_pg"],
    });

    const basicLayout = {
        hasErrors: layout.byName.basic.hasErrors,
        component: (
            <div className="flex flex-col gap-[15px]">
                <forms.Fields form={form} layout={layout.byName.basic} />
            </div>
        ),
    };

    const advancedLayout = {
        hasErrors: layout.byName.advanced.hasErrors,
        component: (
            <div className="flex flex-col gap-[15px]">
                <forms.Fields form={form} layout={layout.byName.advanced} />
            </div>
        ),
    };

    return {form, layout, basicLayout, advancedLayout};
};
